import { defineStore } from 'pinia'
import { toggleCopyOnBody } from '@/util/copypaste_helpers'
import { normalizeUTCTimeFromLocalTz } from '@/util/time'
import { getCurrentUser } from './api_js'
import { differenceInDays } from 'date-fns'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useContentAccessStore } from '@/content_access/content_access.store.ts'
import { useTrackingStore } from '@/tracking/tracking.store'
import { getUserFirstInitial } from './user.utils'

export const useUserStore = defineStore('user', {
  state: () => ({
    fetching: true,
    user: {},
    user_type: 'default',
    admin_schools: [],
    toolkit_account: {},
    license: {},
    super_admin: false,
    pathways_access: false,
    domains: [],
    schools: [],
    domain_upp: false,
    domain_info: {},
    _enableFullstory: false,
    showLicenseSelector: false,
    _callbackQueue: [], // callbacks for when user info is fetched; each one called with true if user was authenticated, otherwise false
    terms_accepted_error: false,
  }),
  actions: {
    setStateFromPayload(data) {
      this.$patch({
        user: data.user || {},
        admin_schools: data.admin_schools || [],
        toolkit_account: data.toolkit_account || {},
        license: data.license || {},
        super_admin: data.super_admin,
        pathways_access: data.pathways_access,
        domains: data.domains || [],
        schools: data.schools || [],
        domain_upp: data.domain_upp,
        domain_info: data.domain_info || {},
        _enableFullstory: data._enable_fullstory
      })
    },
    async getUserInfo() {
      this.fetching = true
      const trackingStore = useTrackingStore()
      try {
        const data = await getCurrentUser()
        this.setStateFromPayload(data)
        const contentAccessStore = useContentAccessStore()
        contentAccessStore.setContentAccessReport(data.content_access_report)
        contentAccessStore.setLibraryQuotasFromTkAccount(data.toolkit_account)
        this.storeAndSetUserContentProfile(data.content_profile)

        this.fetching = false

        contentAccessStore.getUserDownloads()

        trackingStore.initAuthenticatedTracking(data)

        this._callbackQueue.forEach((cb) => {
          cb(true)
        })
        this._callbackQueue = []
        return data
      } catch ({ statusCode, responseJSON }) {
        console.warn(`Could not authenticate user: ${statusCode}`)
        console.dir(responseJSON)
        trackingStore.initUnauthenticatedTracking({ enable_fullstory: true })
        this.storeAndSetUserContentProfile(responseJSON.content_profile)

        this.fetching = false
        this._callbackQueue.forEach((cb) => {
          cb(false)
        })
        this._callbackQueue = []
        return responseJSON
      }
    },
    storeAndSetUserContentProfile(profileInfo) {
      const contentProfileStore = useContentProfileStore()
      contentProfileStore.storeProfile(profileInfo)

      // if we haven't set the "active" content profile yet, initialize it here. We need to make sure it's not set first, in case it's been set elsewhere in the app and thus shouldn't be overwritten
      if (!contentProfileStore.profileId) {
        contentProfileStore.setActiveProfileById(profileInfo.id)
      }
    },
    setLicenseSelectorOpen(flag) {
      this.showLicenseSelector = flag
    },
    pushOnetimeCallback(callback) {
      this._callbackQueue.push(callback)
    }
    // TODO: add acceptTerms
  },
  getters: {
    licenseSubType(state) {
      return state.license.sub_type
    },
    isAuthenticated(state) {
      return !!(state.user && state.user.uuid)
    },
    isSuperAdmin(state) {
      // in general we want to disable users from copying the entire page, but we'll allow it for super admins
      if (!state.super_admin) {
        toggleCopyOnBody(false)
      } else {
        toggleCopyOnBody(true)
      }
      return state.super_admin
    },
    hasLicense(state) {
      return !!(state.license && state.license.uuid && state.license.uuid.length > 0)
    },
    isTrial(state) {
      return state.license && state.license.sub_type === 'trial'
    },
    isPremium(state) {
      return (
        state.license && (state.license.sub_type === 'premium' || state.license.sub_type === 'gold')
      )
    },
    isEnterprise(state) {
      return state.license && state.license.type === 'enterprise'
    },
    isIndividual(state) {
      return state.license && state.license.type === 'individual'
    },
    isSchoolAdmin(state) {
      return state.admin_schools && state.admin_schools.length > 0
    },
    daysLeftInLicense() {
      return Math.max(0, this.licenseEndDateFromToday)
    },

    licenseEndDateFromToday() {
      if (this.isSuperAdmin || !this.isAuthenticated || !this.hasLicense) {
        return NaN
      }

      return differenceInDays(this.license.end_date, normalizeUTCTimeFromLocalTz())
    },
    isEnterpriseEndingSoon() {
      return this.isEnterprise && this.isPremium && this.daysLeftInLicense <= 60
    },
    isLicenseExpired() {
      return this.hasLicense && this.licenseEndDateFromToday < 0
    },
    authInfo() {
      return {
        isAuthenticated: this.isAuthenticated,
        isTrial: this.isTrial,
        isPremium: this.isPremium,
        isSuperAdmin: this.isSuperAdmin,
        isLicenseExpired: this.isLicenseExpired
      }
    },
    ssoProviderIds(state) {
      return state.user?.sso_provider_ids || []
    },
    enforceSSO(state) {
      return state.user?.enforce_sso || false
    },
    hasGoogleOauth(state) {
      return state.user?.oauth_provider === 'google_oauth2'
    },
    userContentProfileId(state) {
      return state.user.content_profile_id
    },
    userId(state) {
      return state.user.uuid
    },
    domainInfo(state) {
      return (state.domains && state.domains[0]) || {}
    },
    isTestUser(state) {
      return state.user.email && state.user.email.indexOf('test.k12.ca.us') > -1
    },
    isGoalbookBusinessUser(state) {
      return (
        state.domains &&
        state.domains.map((domain) => domain.name).indexOf('Goalbook Business') > -1
      )
    },
    isMultiyear(state) {
      return state.domains.some((domain) => domain.flags.includes('multiyear'))
    },
    interactiveGuidesOn(state) {
      return state.domains.some((domain) => domain.flags.includes('interactive_guides_on'))
    },
    hasThreadsAccessOverrideTag(state) {
      return (state.user.tags || []).some(
        (tag) => tag.toLowerCase() === 'threads access (flag override)'
      )
    },
    eventMetadata() {
      return {
        u: this.user.uuid || '',
        ue: this.user.email || '',
        un: this.user.name || '',
        ut: this.user.title || '',
        utz: this.user.timezone || '',
        role: this.user.role || '',
        ust: this.user.state || '',
        d: (this.domains && this.domains[0] && this.domains[0].uuid) || '',
        dn: (this.domains && this.domains[0] && this.domains[0].name) || '',
        s: (this.schools && this.schools.map((s) => s.uuid)) || [],
        sn: (this.schools && this.schools.map((s) => s.name)) || [],
        lu: this.license.uuid || '',
        lt: this.license.type || '',
        lst: this.license.sub_type || '',
        lsd: this.license.start_date || null,
        led: this.license.end_date || null,
        le: this.isLicenseExpired
      }
    },
    userName(state) {
      return state.user.name
    },
    userEmail(state) {
      return state.user.email
    },
    showTrainingButton(state) {
      return state.user.show_training_button || false
    },
    userDomainUuid(state) {
      return state.domains?.[0]?.uuid
    }, 
    getFolderMemberInfo() {
      return (libraryFolder) => {
        if (!libraryFolder) {
          return {
            'unknown-user': {
              name: 'Unknown',
              initials: 'U',
              isCurrentUser: false,
              isOwner: false,
              currentUserIsOwner: false,
              email: null,
              role: null,
            },
          }
        }
        
        const currentUserId = this.userId
        const folderRoles = Array.isArray(libraryFolder?.folder_roles)
          ? libraryFolder.folder_roles
          : []
        const rolesForAvatar = folderRoles.reduce((acc, role, idx) => {
          const key = role.user_uuid || `unknown-user-${idx}`
          const name = role.user_name || 'Unknown'
          const email = role.user_email || null
          const roleName = role.role || null 
          const isOwner = (role.role || '').toLowerCase() === 'owner' // is this role for the owner of the folder
          const isCurrentUser = role.user_uuid === currentUserId // is this role for the current logged in user
          const currentUserIsOwner = isOwner && isCurrentUser // is the current user the owner
          acc[key] = {
            name,
            initials: getUserFirstInitial(name),
            isCurrentUser,
            isOwner,
            currentUserIsOwner,
            email,
            role: roleName,
          }
          return acc
        }, {})
        return rolesForAvatar
      }
    }, 
  }
})
